import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Home as HomeIcon,
  Shield, 
  Truck, 
  Flame, 
  Recycle, 
  Award, 
  Phone, 
  Mail, 
  MapPin,
  ArrowRight,
  CheckCircle,
  Users,
  Calendar } from 'lucide-react';
import HeroSlider from '../components/HeroSlider';
import AnimatedSection from '../components/AnimatedSection';
import CountUpAnimation from '../components/CountUpAnimation';
import PartnersSlider from '../components/PartnersSlider';

const Home = () => {
  const services = [
    {
      icon: <HomeIcon className="w-8 h-8" />,
      title: "Domestic Waste Handling",
      description: "Efficient and eco-friendly management of household waste with proper segregation and disposal"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Medical Waste Disposal",
      description: "Safe disposal of medical and biohazardous waste with full regulatory compliance",
    },
    {
      icon: <Truck className="w-8 h-8" />,
      title: "Transportation & Logistics",
      description: "Secure transportation of hazardous materials with specialized vehicles"
    },
    {
      icon: <Flame className="w-8 h-8" />,
      title: "Incineration Services",
      description: "High-temperature incineration for complete waste destruction"
    },
    {
      icon: <Recycle className="w-8 h-8" />,
      title: "Recycling Solutions",
      description: "Converting waste into valuable resources through sustainable practices"
    }
  ];

  const stats = [
    { number: 200, label: "Clients Served", icon: <Users className="w-5 h-5 sm:w-6 sm:h-6" />, suffix: "+" },
    { number: 5, label: "Years Of Service", icon: <Calendar className="w-5 h-5 sm:w-6 sm:h-6" />, suffix: "+" },
    { number: 99.9, label: "Safety Record", icon: <Shield className="w-5 h-5 sm:w-6 sm:h-6" />, suffix: "%" },
    { number: 24, label: "Emergency Response", icon: <Phone className="w-5 h-5 sm:w-6 sm:h-6" />, suffix: "/7" }
  ];

  const values = [
    { title: "Safety First", description: "Uncompromising commitment to safety standards" },
    { title: "Integrity", description: "Transparent and honest business practices" },
    { title: "Innovation", description: "Cutting-edge waste management solutions" },
    { title: "Sustainability", description: "Environmental responsibility in every action" }
  ];

  return (
    <div className="w-full overflow-x-hidden">
      {/* Hero Slider */}
      <HeroSlider />

      {/* Stats Section */}
      <section className="py-10 sm:py-14 md:py-16 xl:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-in">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-5 md:gap-8">
              {stats.map((stat, index) => (
                <AnimatedSection 
                  key={index} 
                  animation="scale-in" 
                  delay={index * 200}
                  className="text-center"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-blue-50 text-[#295cab] rounded-full mb-3 sm:mb-4 hover-scale icon-bounce">
                    {stat.icon}
                  </div>
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">
                    <CountUpAnimation end={stat.number} suffix={stat.suffix} />
                  </div>
                  <div className="text-gray-600 text-sm sm:text-base">{stat.label}</div>
                </AnimatedSection>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Services Section with Parallax Background */}
      <ParallaxServicesSection services={services} />

      {/* About Preview Section */}
      <section className="py-12 sm:py-16 md:py-20 xl:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10 md:gap-12 items-center">
            <AnimatedSection animation="slide-in-left" className="order-2 lg:order-1">
              <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 xl:mb-8">
                Leading Waste Management Since 2021
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-600 mb-4 sm:mb-6 xl:mb-8">
                BioHaz Waste Solutions is Uganda's premier biohazard and hazardous waste management company, 
                committed to transforming waste into valuable resources while ensuring environmental safety.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6 mb-6 sm:mb-8">
                {values.map((value, index) => (
                  <AnimatedSection 
                    key={index} 
                    animation="fade-in" 
                    delay={index * 100}
                    className="flex items-start space-x-2"
                  >
                    <CheckCircle className="w-5 h-5 text-[#295cab] mt-1 flex-shrink-0 animate-pulse-slow" />
                    <div>
                      <h4 className="font-semibold text-gray-900 text-xs sm:text-sm md:text-base">{value.title}</h4>
                      <p className="text-xs sm:text-sm text-gray-600">{value.description}</p>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
              <Link 
                to="/about" 
                className="inline-flex items-center space-x-2 bg-[#295cab] text-white px-6 py-3 rounded-lg hover:bg-[#1e4a8a] transition-all transform hover:scale-105 text-sm sm:text-base btn-animate hover-glow"
              >
                <span>Learn More About Us</span>
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </Link>
            </AnimatedSection>
            <AnimatedSection animation="slide-in-right" className="relative order-1 lg:order-2">
              <img 
                src="/garbagetruck.jpg" 
                alt="Waste Management Team" 
                className="rounded-xl shadow-2xl w-full hover-scale"
              />
              <div className="absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-6 bg-[#295cab] text-white p-4 sm:p-6 rounded-lg animate-float hover-glow">
                <div className="flex items-center space-x-2">
                  <Award className="w-5 h-5 sm:w-6 sm:h-6 icon-bounce" />
                  <div>
                    <div className="font-semibold text-sm sm:text-base">Certified by</div>
                    <div className="text-xs sm:text-sm">PPDA, NEMA, KCCA, NDA</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Partners Slider Section */}
      <PartnersSlider />

      {/* Contact CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-r from-[#295cab] to-[#1e4a8a] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection animation="fade-in">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 sm:mb-6">Ready to Partner With Us?</h2>
            <p className="text-lg sm:text-xl mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
              Get in touch today for professional waste management solutions tailored to your needs.
            </p>
          </AnimatedSection>
          
          <AnimatedSection animation="slide-in-left" delay={300}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-10">
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-3 hover-scale">
                <Phone className="w-5 h-5 sm:w-6 sm:h-6 icon-bounce" />
                <div className="text-center sm:text-left">
                  <div className="font-semibold text-sm sm:text-base">Call Us</div>
                  <div className="text-sm sm:text-base">+256 393 254458</div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-3 hover-scale">
                <Mail className="w-5 h-5 sm:w-6 sm:h-6 icon-bounce" />
                <div className="text-center sm:text-left">
                  <div className="font-semibold text-sm sm:text-base">Email Us</div>
                  <div className="text-sm sm:text-base break-all">info@biohazwastesolutions.com</div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-3 hover-scale">
                <MapPin className="w-5 h-5 sm:w-6 sm:h-6 icon-bounce" />
                <div className="text-center sm:text-left">
                  <div className="font-semibold text-sm sm:text-base">Visit Us</div>
                  <div className="text-sm sm:text-base">Ntinda, Kampala</div>
                </div>
              </div>
            </div>
          </AnimatedSection>
          
          <AnimatedSection animation="scale-in" delay={600}>
            <Link 
              to="/contact" 
              className="inline-flex items-center space-x-2 bg-white text-[#295cab] px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold hover:bg-blue-50 transition-all transform hover:scale-105 text-sm sm:text-base btn-animate hover-glow"
            >
              <span>Get a Free Quote</span>
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

// Define the Service interface
interface Service {
  icon: React.ReactNode;
  title: string;
  description: string;
  image?: string;
}

// Parallax Services Section Component with Horizontal Scroller
const ParallaxServicesSection = ({ services }: { services: Service[] }) => {
  const parallaxRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const frameId = useRef<number | null>(null);

  // Handle parallax effect
  useEffect(() => {
    const updateParallax = () => {
      if (!parallaxRef.current) return;
      
      const rect = parallaxRef.current.getBoundingClientRect();
      const isInView = rect.top < window.innerHeight && rect.bottom > 0;
      
      if (isInView) {
        const scrollY = window.scrollY || window.pageYOffset;
        const yPos = -((scrollY - rect.top) * 0.1);
        parallaxRef.current.style.backgroundPositionY = `${yPos}px`;
      }
      
      frameId.current = requestAnimationFrame(updateParallax);
    };

    // Start the animation loop
    frameId.current = requestAnimationFrame(updateParallax);

    // Cleanup
    return () => {
      if (frameId.current) {
        cancelAnimationFrame(frameId.current);
      }
    };
  }, []);

  // Handle horizontal scrolling with buttons
  const scroll = (direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const scrollAmount = direction === 'right' ? 300 : -300;
    container.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  };

  return (
    <section className="relative py-16 sm:py-20 overflow-hidden">
      {/* Parallax Background */}
      <div 
        ref={parallaxRef}
        className="absolute inset-0 bg-fixed bg-cover bg-center z-0"
        style={{
          backgroundImage: 'url(/companyassets.jpg)',
          transform: 'translateZ(0)',
          willChange: 'transform',
          transition: 'background-position 0.1s ease-out',
          backgroundPositionY: '0px'
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
      </div>
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="fade-in" className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Our Core Services</h2>
          <p className="text-lg sm:text-xl text-gray-200 max-w-2xl mx-auto px-4">
            Comprehensive waste management solutions tailored to your industry needs
          </p>
        </AnimatedSection>
        
        <div className="relative">
          <button 
            onClick={() => scroll('left')}
            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 rounded-full bg-white/80 hover:bg-white text-[#295cab] shadow-lg flex items-center justify-center hover:scale-110 transition-all duration-300"
            aria-label="Scroll left"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <div 
            ref={scrollContainerRef}
            className="flex space-x-6 sm:space-x-8 overflow-x-auto pb-8 -mx-4 px-4 scrollbar-hide"
            style={{
              scrollSnapType: 'x mandatory',
              scrollPadding: '0 1rem',
              WebkitOverflowScrolling: 'touch',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            {services.map((service, index) => (
              <div 
                key={index}
                className="flex-shrink-0 w-72 sm:w-80 lg:w-96 snap-start"
                style={{ scrollSnapAlign: 'start' }}
              >
                <AnimatedSection 
                  animation="fade-in" 
                  delay={index * 150}
                  className="bg-white/90 backdrop-blur-sm p-0 overflow-hidden rounded-xl shadow-xl hover-lift card-hover hover:bg-white transition-all duration-300 flex flex-col h-full"
                >
                  {service.image && (
                    <div className="h-48 overflow-hidden">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-6 sm:p-8 flex-1 flex flex-col">
                    <div className="text-[#295cab] mb-4 icon-bounce">{service.icon}</div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-700 mb-4 text-sm sm:text-base flex-1">{service.description}</p>
                    <Link 
                      to="/services" 
                      className="text-[#295cab] font-medium hover:text-[#1e4a8a] flex items-center space-x-1 text-sm sm:text-base group mt-auto"
                    >
                      <span>Learn More</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </AnimatedSection>
              </div>
            ))}
          </div>
          
          <button 
            onClick={() => scroll('right')}
            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 rounded-full bg-white/80 hover:bg-white text-[#295cab] shadow-lg flex items-center justify-center hover:scale-110 transition-all duration-300"
            aria-label="Scroll right"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          
          {/* Scroll indicators for mobile */}
          <div className="md:hidden flex justify-center space-x-2 mt-6">
            {services.map((_, index) => (
              <div 
                key={index}
                className="w-2 h-2 rounded-full bg-white/50"
                onClick={() => {
                  if (scrollContainerRef.current) {
                    const container = scrollContainerRef.current;
                    const card = container.children[index] as HTMLElement;
                    card.scrollIntoView({
                      behavior: 'smooth',
                      block: 'nearest',
                      inline: 'start'
                    });
                  }
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Home;